/**
 * Área FINANCEIRO: a aba Financeiro do admin legado e o modal "Registrar pagamento" que o Kanban abre.
 *
 * Cada função reproduz UMA chamada do legado (`portal/admin.html`), com a mesma tabela, as mesmas
 * colunas, os mesmos filtros e a mesma ordenação, e cita a linha de onde veio. Nenhuma consulta foi
 * "melhorada": migração reproduz o legado, e quem decide o que volta é o RLS (ADR 0002).
 *
 * ONDE O LEGADO IGNORA O ERRO, ESTE MÓDULO TAMBÉM DEVOLVE O ERRO, e é a TELA que o ignora: assim a
 * decisão de engolir uma falha fica escrita onde a pessoa vê o efeito, e não escondida aqui dentro.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum.
import {
  clienteDe,
  executar,
  sucesso,
  type FonteDeDados,
  type LinhaCuponsDescontoResgates,
  type LinhaLeadsFunil,
  type LinhaPagamentos,
  type LinhaParcelas,
  type Resultado,
} from '../nucleo.js';

/**
 * Todos os leads do funil, do mais novo para o mais antigo. Legado: `carregarLeads`,
 * `admin.html:6510` a `6513`, `.select('*').order('data_criacao', { ascending: false })`.
 *
 * O Financeiro do legado não consulta leads: ele usa o `leadsCarregados` que o login já trouxe com
 * esta mesma consulta. Aqui cada área carrega o que usa, e a consulta é a mesma, então o conjunto
 * que a tela recebe é o mesmo. O "backfill" de e-mail que o legado faz logo depois (`6522`) é
 * escrita do funil de vendas, e NÃO mora aqui.
 */
export function listarLeadsDoFunil(fonte: FonteDeDados): Promise<Resultado<LinhaLeadsFunil[]>> {
  return executar<LinhaLeadsFunil[]>(
    clienteDe(fonte).from('leads_funil').select('*').order('data_criacao', { ascending: false }),
  );
}

/**
 * Os cupons já aplicados em pagamento, do mais recente para o mais antigo. Legado:
 * `admin.html:8139`, `.select('*').not('aplicado_em', 'is', null).order('aplicado_em', desc)`.
 */
export function listarCuponsAplicados(fonte: FonteDeDados): Promise<Resultado<LinhaCuponsDescontoResgates[]>> {
  return executar<LinhaCuponsDescontoResgates[]>(
    clienteDe(fonte)
      .from('cupons_desconto_resgates')
      .select('*')
      .not('aplicado_em', 'is', null)
      .order('aplicado_em', { ascending: false }),
  );
}

/**
 * Os planos de pagamento dos leads informados, do mais novo para o mais antigo. Legado:
 * `admin.html:8145`, `.select('*').in('lead_id', leadIds).order('criado_em', desc)`.
 */
export function listarPagamentosDosLeads(
  fonte: FonteDeDados,
  leadIds: readonly number[],
): Promise<Resultado<LinhaPagamentos[]>> {
  return executar<LinhaPagamentos[]>(
    clienteDe(fonte)
      .from('pagamentos')
      .select('*')
      // Cópia do array: o supabase-js espera um array mutável, e a tela passa um somente leitura.
      .in('lead_id', [...leadIds])
      .order('criado_em', { ascending: false }),
  );
}

/**
 * As parcelas dos planos informados, pela ordem do número. Legado: `admin.html:8149`,
 * `.select('*').in('pagamento_id', pagIds).order('numero', { ascending: true })`.
 */
export function listarParcelasDosPagamentos(
  fonte: FonteDeDados,
  pagamentoIds: readonly number[],
): Promise<Resultado<LinhaParcelas[]>> {
  return executar<LinhaParcelas[]>(
    clienteDe(fonte)
      .from('parcelas')
      .select('*')
      .in('pagamento_id', [...pagamentoIds])
      .order('numero', { ascending: true }),
  );
}

/** "Confirmar" do marcar como pago. Legado: `admin.html:8474`, `update({ pago: true, data_pagamento })`. */
export function marcarParcelaComoPaga(fonte: FonteDeDados, parcelaId: number, dataPagamento: string): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte).from('parcelas').update({ pago: true, data_pagamento: dataPagamento }).eq('id', parcelaId),
  );
}

/**
 * Volta a parcela para não paga. Legado: "Desmarcar" (`admin.html:8485`) e "Cancelar pagamento" do
 * modal de valor diferente (`admin.html:8580`), os dois com `update({ pago: false, data_pagamento: null })`.
 */
export function desmarcarParcela(fonte: FonteDeDados, parcelaId: number): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte).from('parcelas').update({ pago: false, data_pagamento: null }).eq('id', parcelaId),
  );
}

/**
 * "Salvar" da edição de parcela: novo valor e vencimento, e a parcela volta a não paga. Legado:
 * `admin.html:8498`.
 */
export function editarParcela(
  fonte: FonteDeDados,
  parcelaId: number,
  valor: number,
  dataVencimento: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('parcelas')
      .update({ valor, data_vencimento: dataVencimento, pago: false, data_pagamento: null })
      .eq('id', parcelaId),
  );
}

/**
 * O status atual do lead, lido no banco e não no que a tela tem guardado. Legado:
 * `reabrirPagamentoSeQuitado`, `admin.html:8520`, `.select('status').eq('id', ...).maybeSingle()`.
 * `maybeSingle` porque o lead pode ter sido apagado: aí volta `null`, e não erro.
 */
export function lerStatusDoLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<{ status: string } | null>> {
  return executar<{ status: string } | null>(
    clienteDe(fonte).from('leads_funil').select('status').eq('id', leadId).maybeSingle(),
  );
}

/**
 * Move o lead de etapa. Legado, com o mesmo `update({ status })` nos quatro lugares: quitação
 * (`8542`), reabertura (`8522`), exclusão do plano (`8385`) e confirmação do pagamento (`8113`).
 */
export function mudarStatusDoLead(fonte: FonteDeDados, leadId: number, status: string): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('leads_funil').update({ status }).eq('id', leadId));
}

/**
 * "Confirmar mesmo assim" do valor diferente: grava a observação e o total pago como o novo
 * `valor_total`. Legado: `quitarLead`, `admin.html:8538`. O legado não confere o erro desta
 * gravação; a tela decide igual.
 */
export function gravarQuitacaoComDiferenca(
  fonte: FonteDeDados,
  pagamentoId: number,
  observacao: string | null,
  valorTotal: number,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte).from('pagamentos').update({ observacao, valor_total: valorTotal }).eq('id', pagamentoId),
  );
}

/**
 * A etapa em que o lead estava antes de ir para "Pagamento a receber", lida no histórico. Legado:
 * `excluirPlanoPagamento`, `admin.html:8376` a `8382`. `maybeSingle` porque lead antigo pode não ter
 * histórico, e aí a tela usa "com_juridico", como o legado.
 */
export function lerEtapaAnteriorAoPagamento(
  fonte: FonteDeDados,
  leadId: number,
): Promise<Resultado<{ status_anterior: string | null } | null>> {
  return executar<{ status_anterior: string | null } | null>(
    clienteDe(fonte)
      .from('leads_funil_status_historico')
      .select('status_anterior')
      .eq('lead_id', leadId)
      .eq('status_novo', 'pagamento_a_receber')
      .order('mudado_em', { ascending: false })
      .limit(1)
      .maybeSingle(),
  );
}

/**
 * Apaga o plano de pagamento de um lead e devolve o cupom aplicado a pendente. Legado:
 * `excluirPagamentoDoLead`, `admin.html:6953` a `6967`, na mesma sequência de cinco chamadas.
 *
 * O LEGADO NÃO CONFERE O ERRO DE NENHUMA DELAS, e segue para a próxima. Esta função reproduz isso: a
 * sequência inteira roda, e o resultado é sempre sucesso. Mudar para parar no primeiro erro seria
 * corrigir o legado, e isso é decisão do fundador.
 */
export async function excluirPagamentoDoLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<null>> {
  const sb = clienteDe(fonte);
  // O plano do lead (`6954`): sem plano, nada a apagar, como o `if (!pag) return` do legado.
  const pag = await executar<{ id: number } | null>(
    sb.from('pagamentos').select('id').eq('lead_id', leadId).maybeSingle(),
  );
  if (!pag.ok || !pag.dados) return sucesso(null);
  // As parcelas primeiro (`6956`), porque elas apontam para o plano.
  await executar<null>(sb.from('parcelas').delete().eq('pagamento_id', pag.dados.id));
  // O plano (`6957`).
  await executar<null>(sb.from('pagamentos').delete().eq('id', pag.dados.id));
  // O resgate de cupom aplicado mais recente desse lead (`6961` a `6963`).
  const resgate = await executar<{ id: number } | null>(
    sb
      .from('cupons_desconto_resgates')
      .select('id')
      .eq('lead_id', leadId)
      .not('aplicado_em', 'is', null)
      .order('aplicado_em', { ascending: false })
      .limit(1)
      .maybeSingle(),
  );
  // Volta a pendente (`6965`), senão o cupom some do modal quando o pagamento for refeito.
  if (resgate.ok && resgate.dados) {
    await executar<null>(
      sb.from('cupons_desconto_resgates').update({ aplicado_em: null, valor_aplicado: null }).eq('id', resgate.dados.id),
    );
  }
  return sucesso(null);
}

/* ---------------------------------------------------------------------------------------------
 * O MODAL "REGISTRAR PAGAMENTO" (`admin.html:7855` a `8127`)
 * ------------------------------------------------------------------------------------------- */

/**
 * O preço padrão dos produtos informados (chaves de `precos_produtos`). Legado: `admin.html:7891`,
 * `.select('produto, preco').in('produto', paramKeys)`.
 */
export function listarPrecosDosProdutos(
  fonte: FonteDeDados,
  produtos: readonly string[],
): Promise<Resultado<{ produto: string; preco: number | null }[]>> {
  return executar<{ produto: string; preco: number | null }[]>(
    clienteDe(fonte).from('precos_produtos').select('produto, preco').in('produto', [...produtos]),
  );
}

/** O cupom embutido no resgate pendente, como o `select` com relação do legado devolve. */
export interface CupomDoResgate {
  readonly codigo: string;
  readonly tipo_desconto: string;
  readonly valor_desconto: number;
  readonly ativo: boolean;
  readonly validade: string | null;
}

/** O resgate pendente de um lead, com o cupom junto. */
export interface ResgatePendente {
  readonly id: number;
  readonly cupom_id: number;
  // O PostgREST devolve a relação muitos-para-um como objeto; `null` se o cupom foi apagado.
  readonly cupons_desconto: CupomDoResgate | null;
}

/**
 * O cupom que a cliente informou no portal e ainda não foi aplicado. Legado: `admin.html:7912` a
 * `7917`, com `.is('aplicado_em', null).maybeSingle()`: sem resgate pendente, `null` e não erro.
 */
export function lerResgatePendenteDoLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<ResgatePendente | null>> {
  return executar<ResgatePendente | null>(
    clienteDe(fonte)
      .from('cupons_desconto_resgates')
      .select('id, cupom_id, cupons_desconto(codigo, tipo_desconto, valor_desconto, ativo, validade)')
      .eq('lead_id', leadId)
      .is('aplicado_em', null)
      .maybeSingle(),
  );
}

/** O plano que o lead já tem, se tiver. Legado: `admin.html:8091`, `.select('id').eq('lead_id', ...).maybeSingle()`. */
export function lerPagamentoDoLead(fonte: FonteDeDados, leadId: number): Promise<Resultado<{ id: number } | null>> {
  return executar<{ id: number } | null>(clienteDe(fonte).from('pagamentos').select('id').eq('lead_id', leadId).maybeSingle());
}

/** Os campos que o modal grava no plano. */
export interface CamposDoPagamento {
  readonly valor_total: number;
  readonly forma_pagamento: string;
  readonly observacao: string | null;
}

/**
 * Plano já existente: atualiza, com a hora da atualização. Legado: `admin.html:8094`.
 * `atualizadoEm` vem de quem chama, como o `new Date().toISOString()` do legado, para a regra da
 * hora ficar na tela e esta função não depender do relógio.
 */
export function atualizarPagamento(
  fonte: FonteDeDados,
  pagamentoId: number,
  campos: CamposDoPagamento,
  atualizadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('pagamentos')
      .update({ ...campos, atualizado_em: atualizadoEm })
      .eq('id', pagamentoId),
  );
}

/**
 * Apaga as parcelas antigas antes de gravar as novas. Legado: `admin.html:8097`, que não confere o
 * erro desta chamada.
 */
export function apagarParcelasDoPagamento(fonte: FonteDeDados, pagamentoId: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('parcelas').delete().eq('pagamento_id', pagamentoId));
}

/**
 * Lead sem plano: insere e devolve o `id`. Legado: `admin.html:8099`. `.single()` depois de uma
 * escrita é o certo: a linha acabou de ser criada.
 */
export function inserirPagamento(
  fonte: FonteDeDados,
  leadId: number,
  campos: CamposDoPagamento,
): Promise<Resultado<{ id: number }>> {
  return executar<{ id: number }>(
    clienteDe(fonte)
      .from('pagamentos')
      .insert({ lead_id: leadId, ...campos })
      .select('id')
      .single(),
  );
}

/** Uma parcela a gravar, no formato do legado. */
export interface ParcelaNova {
  readonly pagamento_id: number;
  readonly numero: number;
  readonly valor: number;
  readonly data_vencimento: string;
}

/** Grava as parcelas do plano de uma vez. Legado: `admin.html:8106` a `8111`. */
export function inserirParcelas(fonte: FonteDeDados, parcelas: readonly ParcelaNova[]): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('parcelas').insert([...parcelas]));
}

/**
 * Marca o cupom pendente como aplicado, com o desconto que entrou. Legado: `admin.html:8118` a
 * `8120`, sem conferir o erro.
 */
export function marcarCupomComoAplicado(
  fonte: FonteDeDados,
  resgateId: number,
  valorAplicado: number,
  aplicadoEm: string,
): Promise<Resultado<null>> {
  return executar<null>(
    clienteDe(fonte)
      .from('cupons_desconto_resgates')
      .update({ aplicado_em: aplicadoEm, valor_aplicado: valorAplicado })
      .eq('id', resgateId),
  );
}
